import React, { ReactNode, useEffect, useState } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { COUPON_COOKIE_EXPIRATION_HOURS, DEV_MODE, getCookieDomain, removeCookie } from '../../lib/util';
import { getNanoVariantPetName, hasNanoVariantPetName } from '../../lib/util/petUrlParams';
import useRescueActivationFee from '../../hooks/useRescueActivationFee';
import { useSelector } from 'react-redux';
import useRescueBundleSkus from '../../hooks/useRescueBundleSkus';
import { cartFitsRescueCheckout, rescueOnePageEligible } from '../../lib/rescueCheckout';
import { useIsGift } from '../../lib/util/isGift';
import * as types from '../../types';
import { rescueLinkNamedSpecies } from './rescueLinkSpecies';
import styles from './RescueBanner.module.scss';

export const RESCUE_BANNER_COOKIE = 'rescue_banner_data';
export const RESCUE_BANNER_DISMISSED_COOKIE = 'rescue_banner_dismissed';
/**
 * Matched to `COUPON_COOKIE_EXPIRATION_HOURS`, which is also the server's entitlement TTL. The
 * banner cookie is what puts the adoption framing on the page - the co-brand header, "included with
 * your adoption", the suppressed promo field - while the discount behind it rides on the coupon and
 * entitlement cookies. Outliving them left days 4 to 7 promising a gift the cart could no longer
 * apply. Raising the other two instead would need the server TTL raised with them.
 */
export const RESCUE_BANNER_COOKIE_EXPIRATION_DAYS = COUPON_COOKIE_EXPIRATION_HOURS / 24;

interface RescueBannerData {
  orgName: string | null;
  offer: string;
}

/**
 * Is this shopper an adopter, i.e. is the gift real for them. Every rescue surface - banner, offer
 * box, chip module, testimonials, faq, the suppressed promo field, the cart's renewal and discount
 * rows - reads this, so they move together. Entry is governed by the signed entitlement, server-side.
 */
export function showRescueGwaVariant(): boolean {
  return !!Cookies.get(RESCUE_BANNER_COOKIE);
}

/** Should this adopter see the redesigned adoption page. Same gate as `showRescueGwaVariant()`. */
export function showRescuePdpRedesign(): boolean {
  return showRescueGwaVariant();
}

/**
 * Whether this visitor gets the one-page rescue checkout test at all: on the Mini page and eligible
 * (`rescueOnePageEligible`). The page (`MiniProductRoute`) and this banner both read the test's flag only
 * when it holds, so one check decides who is enrolled and neither can enroll anyone the other would not.
 * Lives here, beside the rescue gate it builds on, so the banner can use it without an import cycle.
 */
export function useRescueOnePageEligible() {
  const { pathname } = useLocation();
  const isGift = useIsGift();
  const skus = useRescueBundleSkus();
  const cartFits = useSelector((state: types.AppState) => cartFitsRescueCheckout(state.storeShop.cart, skus));

  return (
    pathname.startsWith('/products/fi-mini') &&
    rescueOnePageEligible({
      rescueAdopter: showRescuePdpRedesign(),
      isGift,
      speciesNamed: rescueLinkNamedSpecies(),
      cartFits,
    })
  );
}

export function rescueBannerDismissed(): boolean {
  return !!Cookies.get(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * records the dismissal in its own cookie and leaves `rescue_banner_data` in place. that cookie is
 * also the rescue-variant gate (`showRescueGwaVariant`), so clearing it here would turn off the
 * whole rescue experience for the session, not just this banner. scoped like the other shop cookies
 * so the dismissal holds across subdomains, and expires with the rescue context it belongs to.
 */
export function dismissRescueBanner() {
  Cookies.set(RESCUE_BANNER_DISMISSED_COOKIE, '1', {
    path: '/',
    expires: RESCUE_BANNER_COOKIE_EXPIRATION_DAYS,
    domain: getCookieDomain(),
    secure: !DEV_MODE,
  });
}

/**
 * `removeCookie` rather than `Cookies.remove`: the dismissal cookie is written against the shared
 * parent domain, and a removal that omits that domain silently does nothing in production.
 */
export function clearRescueBannerDismissal() {
  removeCookie(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * Reads rescue banner data from a cookie set by the /rescue/:slug landing route.
 * Renders the co-brand header block on the shop page: eyebrow naming the rescue, headline stating
 * the offer. Re-checks the cookie on every navigation so it picks up the value after redirect.
 *
 * Deliberately not dismissible: the rescue connection is the whole reason this traffic converts,
 * and dismissal used to read as "turn the rescue experience off". The dismissal cookie helpers
 * above stay exported for the landing route and existing tests, but nothing consults them here.
 */
/** The co-brand block every rescue surface has carried: eyebrow over the headline that states the offer. */
function FullRescueBanner({ eyebrow, headline }: { eyebrow: string; headline: string }) {
  return (
    <div className={styles.cobrand}>
      <div className={styles.cobrandInner}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h3 className={styles.headline}>{headline}</h3>
      </div>
    </div>
  );
}

/**
 * The one-page rescue checkout's strip: the rescue's name and whose offer this is, on one slim line.
 * The page's own headline and summary state the offer and the price, so the band does not repeat them.
 */
function CompactRescueBanner({ eyebrow, offer }: { eyebrow: string; offer: string }) {
  return (
    <div className={styles.compact}>
      <span className={styles.compactEyebrow}>{eyebrow}</span>
      <span className={styles.compactOffer}>{offer}</span>
    </div>
  );
}

/**
 * On /products/fi-mini the band follows the one-page checkout test: the variant gets the slim strip
 * its page was designed with, control keeps the full band. Mounted only when `useRescueOnePageEligible`
 * holds, the same check the page's own reader (`MiniProductRoute`) uses, so reading the flag here
 * enrolls no one the page would not.
 */
function MiniPageRescueBanner({ full, compact }: { full: ReactNode; compact: ReactNode }) {
  const onePage = useFeatureValue('ecom-rescue-one-page-checkout', false);
  return <>{onePage ? compact : full}</>;
}

/**
 * Reads rescue banner data from a cookie set by the /rescue/:slug landing route.
 * Renders the co-brand header block on the shop page: eyebrow naming the rescue, headline stating
 * the offer. Re-checks the cookie on every navigation so it picks up the value after redirect.
 *
 * Deliberately not dismissible: the rescue connection is the whole reason this traffic converts,
 * and dismissal used to read as "turn the rescue experience off". The dismissal cookie helpers
 * above stay exported for the landing route and existing tests, but nothing consults them here.
 */
export default function RescueBanner() {
  const location = useLocation();
  const { t } = useTranslation();
  const activationFee = useRescueActivationFee();
  // the page's own eligibility check, so the band can never enroll anyone the page would not
  const onePageEligible = useRescueOnePageEligible();
  const [bannerData, setBannerData] = useState<RescueBannerData | null>(null);

  useEffect(() => {
    const raw = Cookies.get(RESCUE_BANNER_COOKIE);
    if (raw) {
      try {
        setBannerData(JSON.parse(raw));
      } catch {
        // ignore malformed cookie
      }
    }
  }, [location.pathname]);

  if (!bannerData) {
    return null;
  }

  // a program with no org name gets the generic partner eyebrow, never "Your rescue". the headline
  // is shared: it states the offer as an attribute of the adoption, so no payer has to be named.
  //
  // it is the one line above the fold on a phone, so it carries the whole deal: the GPS tracker comes
  // with the adoption and activation is the only charge. it avoids "free" because activation is still
  // charged. the fee comes from the market's plan, never the copy - when it cannot be resolved the line
  // still promises the tracker, just without a number.
  //
  // personalized only while the adopter link's pet params are in the url; the generic line is the
  // fallback everywhere else, including after navigation drops the query string
  const named = hasNanoVariantPetName();
  const petName = named ? getNanoVariantPetName() : undefined;
  const eyebrow = bannerData.orgName
    ? t('rescue_banner_eyebrow', { name: bannerData.orgName })
    : t('rescue_banner_eyebrow_generic');
  const headline = activationFee
    ? named
      ? t('rescue_banner_headline_with_fee_named', { petName, fee: activationFee })
      : t('rescue_banner_headline_with_fee', { fee: activationFee })
    : named
      ? t('rescue_banner_headline_named', { petName })
      : t('rescue_banner_headline');
  const full = <FullRescueBanner eyebrow={eyebrow} headline={headline} />;

  if (onePageEligible) {
    const offer = named ? t('rescue_banner_compact_offer_named', { petName }) : t('rescue_banner_compact_offer');
    return <MiniPageRescueBanner full={full} compact={<CompactRescueBanner eyebrow={eyebrow} offer={offer} />} />;
  }

  return full;
}
